'use client';

import React, { useState, useEffect, useCallback } from 'react';
import styles from './SettingsLayout.module.scss';

// Import extracted components from barrel export
import {
    ArrowHead,
    Divider,
    DropdownButton,
    LanguageButton,
    SettingButton,
    SettingToggle
} from '../../../../components/SettingTab';
// Import the SVG icons from utils/icons
import { 
    AdminSettingTabIcon, 
    EyeFishIcon, 
    EyeIcon, 
    EyePaniniIcon, 
    HDSDSettingTabIcon, 
    HotSpotSettingTabIcon, 
    RestartCacheIcon, 
    SettingIcon, 
    Turn360 
} from '../../../../utils/icons';

interface SettingsLayoutProps {
    isOpen: boolean;
    onClose: () => void;
}

/**
 * SettingsLayout component that displays the main settings interface
 * matching the Figma design from node-id=2237-140806
 * 
 * This component has been refactored to use modular components from
 * the SettingTab directory:
 * - ArrowHead: Decorative arrow icons on left/right sides
 * - Divider: Horizontal separator lines
 * - SettingButton: Standard button with icon and text
 * - SettingToggle: Toggle switch with icon and text
 * - DropdownButton: Button with dropdown arrow indicator 
 * - LanguageButton: Special button for language selection
 * - SettingsButton: Main settings control button at bottom
 */
const SettingsLayout: React.FC<SettingsLayoutProps> = ({ isOpen, onClose }) => {
    // Settings states
    const [darkMode, setDarkMode] = useState(false);
    const [vrMode, setVrMode] = useState(false);
    const [navigation360, setNavigation360] = useState(false);
    const [autoRotate, setAutoRotate] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [voiceOver, setVoiceOver] = useState(false);
    const [backgroundMusic, setBackgroundMusic] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState<'en' | 'vn'>('vn');
    const viewOptions = [
        {
            value: 'normal',
            label: 'Mắt thường',
            icon: "👁️"
        },
        {
            value: 'fish_eye',
            label: 'Mắt cá',
            icon: '🐟'
        },
        {
            value: 'panini',
            label: 'Góc nhìn Panini',
            icon: '🔍'
        }
    ];
    const [selectedView, setSelectedView] = useState('normal');

    // Handle toggle change
    const handleToggleChange = useCallback((setter: React.Dispatch<React.SetStateAction<boolean>>) => {
        return () => setter(prev => !prev);
    }, []);

    // Handle fullscreen toggle
    const handleFullscreenToggle = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable fullscreen: ${err.message}`);
            });
            setFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
                setFullscreen(false);
            }
        }
    }, []);

    // Listen for fullscreen change events
    useEffect(() => {
        const handleFullscreenChange = () => {
            setFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Handle language change
    const toggleLanguage = () => {
        setCurrentLanguage(prev => prev === 'vn' ? 'en' : 'vn');
    };

    // Handle restart
    const handleRestart = () => {
        // Clear local storage cache
        localStorage.clear();
        // Reload the page
        window.location.reload();
    };

    // Apply dark mode
    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark-mode');
        } else {
            document.documentElement.classList.remove('dark-mode');
        }
    }, [darkMode]);

    if (!isOpen) return null;

    return (
        <div className={styles.settingsContainer} role="dialog" aria-label="Settings panel">
            {/* Main container with glass effect */}
            <div className={styles.settingsBar}>
                {/* Left arrow head decoration */}
                <ArrowHead direction="top" />

                {/* Main content area */}
                <div className={styles.contentArea}>
                    {/* Restart button */}
                    <SettingButton
                        icon={
                            <RestartCacheIcon />
                        }
                        text="Xóa Cache & khởi động lại"
                        onClick={handleRestart}
                    />

                    {/* Admin icon */}
                    <SettingButton
                        icon={
                            <AdminSettingTabIcon />
                        }
                        text="Trình quản lý Admin"
                    />

                    {/* User manual button */}
                    <SettingButton
                        icon={
                            <HDSDSettingTabIcon />
                        }
                        text="Hướng dẫn sử dụng"
                    />

                    {/* Divider */}
                    <Divider />

                    {/* Language button */}
                    <LanguageButton
                        currentLanguage={currentLanguage}
                        onClick={toggleLanguage}
                    />

                    {/* Divider */}
                    <Divider />

                    {/* Dark mode toggle */}
                    <SettingToggle
                        icon={
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="5" stroke="#656565" strokeWidth="3" />
                                <path d="M12 3V1M12 23V21M3 12H1M23 12H21M5 5L3.5 3.5M19 5L20.5 3.5M19 19L20.5 20.5M5 19L3.5 20.5" stroke="#656565" strokeWidth="3" strokeLinecap="round" />
                            </svg>
                        }
                        text="Giao diện sáng/tối"
                        isChecked={darkMode}
                        onChange={handleToggleChange(setDarkMode)}
                    />

                    {/* VR mode toggle */}
                    <SettingToggle
                        icon={
                            <img src="/images/VRSettingTab.svg" alt="" width={24} height={24} />
                        }
                        text="Chế độ VR"
                        isChecked={vrMode}
                        onChange={handleToggleChange(setVrMode)}
                    />

                    {/* Panorama view dropdown */}
                    <DropdownButton
                        icon={<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" fill="#656565" />
                            <circle cx="12" cy="12" r="3" fill="#FFFFFF" />
                        </svg>}
                        text="Chuyển góc nhìn"
                        options={viewOptions}
                        selected={selectedView}
                        onSelect={setSelectedView}
                    />

                    {/* 360 Navigation toggle */}
                    <SettingToggle
                        icon={
                            <HotSpotSettingTabIcon />
                        }
                        text="Điều hướng 360"
                        isChecked={navigation360}
                        onChange={handleToggleChange(setNavigation360)}
                    />

                    {/* Auto rotate toggle */}
                    <SettingToggle
                        icon={
                            <Turn360 />
                        }
                        text="Tự động xoay"
                        isChecked={autoRotate}
                        onChange={handleToggleChange(setAutoRotate)}
                    />

                    {/* Fullscreen toggle */}
                    <SettingToggle
                        icon={
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4 8V4H8" stroke="#656565" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4 16V20H8" stroke="#656565" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M16 4H20V8" stroke="#656565" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M16 20H20V16" stroke="#656565" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        }
                        text="Toàn màn hình"
                        isChecked={fullscreen}
                        onChange={handleFullscreenToggle}
                    />

                    {/* Voice narration toggle */}
                    <SettingToggle
                        icon={
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 1C11.2044 1 10.4413 1.31607 9.87868 1.87868C9.31607 2.44129 9 3.20435 9 4V12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12V4C15 3.20435 14.6839 2.44129 14.1213 1.87868C13.5587 1.31607 12.7956 1 12 1Z" stroke="#656565" strokeWidth="3" />
                                <path d="M19 10V12C19 13.8565 18.2625 15.637 16.9497 16.9497C15.637 18.2625 13.8565 19 12 19C10.1435 19 8.36301 18.2625 7.05025 16.9497C5.7375 15.637 5 13.8565 5 12V10" stroke="#656565" strokeWidth="3" />
                                <path d="M12 19V23" stroke="#656565" strokeWidth="3" />
                                <path d="M8 23H16" stroke="#656565" strokeWidth="3" />
                            </svg>
                        }
                        text="Thuyết minh tự động"
                        isChecked={voiceOver}
                        onChange={handleToggleChange(setVoiceOver)}
                    />

                    {/* Background music toggle */}
                    <SettingToggle
                        icon={
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="6" cy="18" r="3" stroke="#656565" strokeWidth="2" />
                                <circle cx="16" cy="16" r="3" stroke="#656565" strokeWidth="2" />
                                <path d="M9 18V6L19 4V16" stroke="#656565" strokeWidth="2" />
                            </svg>
                        }
                        text="Nhạc nền"
                        isChecked={backgroundMusic}
                        onChange={handleToggleChange(setBackgroundMusic)}
                    />
                </div>

                {/* Right arrow decoration */}
                <ArrowHead direction="bot" />

                {/* Settings button on bottom */}
                <SettingButton
                    icon={
                        <SettingIcon />
                    }
                    text="Cài đặt"
                    onClick={onClose}
                />
            </div>
        </div>
    );
};

export default SettingsLayout;
